/**
 * Schedule View — an appointment shaped for the visit checklist card, built from something that is NOT a
 * `/schedule-view/day` appointment.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * WHY THIS EXISTS (Practice Ops, 2026-10-30, plan §4). VisitChecklistCard takes `appt`, one appointment from
 * /schedule-view/day, and reads `patient.FName/LName`, `AptDateTime`, `AptLength`, `ProcDescript`, `Note`.
 * Practice Ops draws that same card from a board card (a patient on the live board) plus the appointment details
 * `POST /get-practice-pulse-ops` answers — it never loads a Schedule View day (an office without "Schedule - Chart
 * Audit" is refused it). This is the minimal adapter so the card can be mounted without ChecklistLayer or any
 * Schedule View page state. The categories and the ticks are NOT built here: checklist.js `checklistsForDay`
 * already takes `[{ AptNum, PatNum }]` and is called with the real functions.
 *
 *   visitAppointment({ aptNum, patNum, first, last, detail, scheduledText, procDescript, note, opNum, provNum })
 *
 *   detail         one row of the details reply ({ AptNum, PatNum, Note, ProcDescript, ProvNum, Op, Pattern,
 *                  AptDateTime }) or null. Its fields win; the loose arguments are the fallbacks.
 *   scheduledText  'YYYY-MM-DD HH:mm:ss' at the practice, used only when `detail` has no AptDateTime
 *
 * AptLength comes from OD's `Pattern` — one character per 5 minutes — so 'XXXXXX' is 30 minutes. No usable pattern ⇒
 * no AptLength, and the card's own default (appointmentLength) applies.
 */

/** Minutes an OD appointment pattern covers: 5 per character. 0 for anything that is not a pattern. */
export function patternMinutes(pattern) {
  if (typeof pattern !== 'string') return 0;
  const trimmed = pattern.trim();
  return trimmed === '' ? 0 : trimmed.length * 5;
}

function str(value) {
  return typeof value === 'string' ? value : '';
}

export function visitAppointment(input) {
  const i = input && typeof input === 'object' ? input : {};
  const d = i.detail && typeof i.detail === 'object' ? i.detail : {};
  const length = patternMinutes(d.Pattern);
  const appt = {
    AptNum: i.aptNum === undefined ? null : i.aptNum,
    PatNum: i.patNum === undefined ? null : i.patNum,
    AptDateTime: str(d.AptDateTime) || str(i.scheduledText) || null,
    ProcDescript: typeof d.ProcDescript === 'string' ? d.ProcDescript : str(i.procDescript),
    Note: typeof d.Note === 'string' ? d.Note : str(i.note),
    Op: d.Op !== undefined && d.Op !== null ? d.Op : (i.opNum === undefined ? null : i.opNum),
    ProvNum: d.ProvNum !== undefined && d.ProvNum !== null ? d.ProvNum : (i.provNum === undefined ? null : i.provNum),
    patient: { FName: str(i.first), LName: str(i.last) },
  };
  if (length > 0) appt.AptLength = length;
  return appt;
}
